import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2, Plus, Sparkles, Film, AlertTriangle } from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageTitle } from "@/hooks/useMimis";
import { useEntitlement, creditLabel, fileUrl, downloadVideo } from "@/hooks/useVideo";
import { PaywallDialog } from "@/components/video/PaywallDialog";

const FASI = { script: "Sto scrivendo lo script...", voce: "Sto creando la voce...", montaggio: "Sto montando il video..." };
const inProgress = (v) => v.stato === "in_coda" || v.stato === "in_corso";

const Pending = ({ v }) => (
  <div className="grid h-full place-items-center px-3 text-center text-sm text-zinc-300" data-testid={`video-pending-${v.id}`}>
    <div>
      <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#0066FF]" />
      <p className="mt-3">{v.stato === "in_coda" ? "In coda..." : FASI[v.fase] || "In creazione..."}</p>
      <p className="mt-1 text-xs text-zinc-400">{v.progresso ?? 0}%</p>
    </div>
  </div>
);

const Failed = ({ v }) => (
  <div className="grid h-full place-items-center px-3 text-center text-sm text-zinc-300" data-testid={`video-failed-${v.id}`}>
    <div>
      <AlertTriangle className="mx-auto h-6 w-6 text-amber-400" />
      <p className="mt-3 font-semibold text-white">Non riuscito</p>
      <p className="mt-1 text-xs text-zinc-300">Non conta come credito. Riprova.</p>
    </div>
  </div>
);

const VideoCard = ({ v }) => (
  <article className="card-soft overflow-hidden p-2 sm:p-3" data-testid={`video-card-${v.id}`}>
    <div className="aspect-[9/16] overflow-hidden rounded-2xl bg-[#111111]">
      {v.stato === "pronto" ? (
        <video src={`${fileUrl(v.video_url)}#t=0.5`} controls playsInline preload="metadata" className="h-full w-full object-cover" />
      ) : v.stato === "errore" ? <Failed v={v} /> : <Pending v={v} />}
    </div>
    <div className="px-1 pb-1 pt-3 sm:px-2 sm:pb-2 sm:pt-4">
      <p className="font-display line-clamp-2 text-sm font-bold leading-snug sm:text-base">{v.titolo || v.idea}</p>
      <p className="mt-1 text-xs text-[#52525b] sm:text-sm">{formatDate(v.created_at)}</p>
      {v.stato === "pronto" && (
        <button onClick={() => downloadVideo(v)} className="btn-ghost mt-4 h-11 w-full text-sm" data-testid={`download-${v.id}`}><Download className="h-4 w-4" /> Scarica</button>
      )}
    </div>
  </article>
);

export default function Dashboard() {
  const { user, loading, login } = useAuth();
  const { ent, refresh } = useEntitlement();
  const [videos, setVideos] = useState(null);
  const [paywall, setPaywall] = useState(false);
  usePageTitle("I miei video | MIMIS", true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/videos");
      setVideos(Array.isArray(r?.data) ? r.data : []);
    } catch (e) {
      setVideos((cur) => cur ?? []);
    }
  }, []);

  useEffect(() => {
    if (loading || !user) return;
    load();
  }, [user, loading, load]);

  const pending = (videos ?? []).some(inProgress);
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(async () => {
      await load();
      refresh();
    }, 3000);
    return () => clearInterval(t);
  }, [pending, load, refresh]);

  if (!loading && !user)
    return (
      <main className="grid min-h-[80vh] place-items-center bg-[#F5F5F7] px-5 pt-24" data-testid="dashboard-login-required">
        <div className="card-soft max-w-md p-9 text-center hover:translate-y-0">
          <p className="font-display text-2xl font-bold">Accedi per vedere i tuoi video</p>
          <button onClick={() => login("/dashboard")} className="btn-blu mt-7 h-12 px-6 text-sm" data-testid="dashboard-login-button">Accedi con Google</button>
        </div>
      </main>
    );

  return (
    <main className="min-h-[80vh] bg-[#F5F5F7] px-5 pb-24 pt-32 sm:px-8" data-testid="dashboard-page">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="eyebrow">Dashboard</span>
            <h1 className="font-display mt-3 text-4xl font-bold tracking-tight sm:text-5xl">I miei video</h1>
          </div>
          <Link to="/tool/mimis-video" className="btn-blu h-12 px-6 text-sm" data-testid="dashboard-new-video"><Plus className="h-4 w-4" /> Nuovo video</Link>
        </div>
        <div className="mt-8 flex flex-col gap-4 rounded-[1.5rem] bg-[#111111] p-6 text-white shadow-[0_20px_50px_-30px_rgba(17,17,17,0.6)] sm:flex-row sm:items-center sm:justify-between" data-testid="credits-card">
          <div className="flex items-center gap-4">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#0066FF]"><Sparkles className="h-5 w-5" /></span>
            <p className="font-display text-lg font-bold sm:text-xl">Crediti rimasti: <span data-testid="credits-label">{creditLabel(ent)}</span></p>
          </div>
          {ent && !ent.pro && (
            <button onClick={() => setPaywall(true)} className="btn-blu h-11 px-5 text-sm" data-testid="dashboard-unlock-button">{ent.abbonato ? "Passa a Pro" : "Scegli un piano"}</button>
          )}
        </div>
        {videos === null ? (
          <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#0066FF]" /></div>
        ) : videos.length === 0 ? (
          <div className="card-soft mt-8 p-12 text-center hover:translate-y-0" data-testid="dashboard-empty">
            <Film className="mx-auto h-9 w-9 text-[#0066FF]" />
            <p className="font-display mt-4 text-2xl font-bold">Ancora nessun video</p>
            <p className="mt-2 text-[#52525b]">Scrivi un'idea e in circa 60 secondi hai il tuo primo video.</p>
            <Link to="/tool/mimis-video" className="btn-blu mt-7 h-12 px-6 text-sm" data-testid="dashboard-empty-cta">Crea il primo video</Link>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4" data-testid="videos-grid">
            {videos.map((v) => <VideoCard key={v.id} v={v} />)}
          </div>
        )}
      </div>
      <PaywallDialog open={paywall} onOpenChange={setPaywall} limit={!!ent?.abbonato} />
    </main>
  );
}
